import { InventoryStateHelper } from '../Inventory/InventoryStateHelper';
import { IPlayerState } from '../IPlayerState';
import { PlayerStateHelper } from '../PlayerStateHelper';
import { ProgressStateHelper } from '../Progress/ProgressStateHelper';
import { IFriendData } from 'Player/PlayerProperty/Social/IPlayerSocialData';
import { FRIEND_GIFT_REWARD } from 'Constants/Economy';
import { TimerType } from 'System/Timer/TimerTypes';
import DateHelper from 'Utility/DateHelper';
import { FRIEND_ALT_GIFT_TIMER_COOLDOWN, FRIEND_GIFT_TIMER_COOLDOWN } from 'Constants/Timers';
import { restoreDate } from 'Player/ParseHelper';

export namespace SocialStateHelper {
    /**
     * Initialize friend data if it does not exists.
     * @param playerState
     * @param friendId
     */
    export const getFriendData = (
        playerState: IPlayerState,
        friendId: string
    ): { playerState: IPlayerState; friendData: IFriendData } => {
        let friendData: IFriendData = playerState.social.friendData.find(friend => friend.playerId === friendId);
        // Find friend data. Initialize if not yet stored.
        if (!friendData) {
            friendData = {
                playerId: friendId,
                currencyRequestTimer: null,
                currencySendTimer: null,
                giftCooldown: null,
                helperTimer: null,
                altGiftCooldown: null,
            };
            // Add new friend.
            playerState = {
                ...playerState,
                social: {
                    ...playerState.social,
                    friendData: [...playerState.social.friendData, friendData],
                },
            };
        }

        return { playerState: playerState, friendData: friendData };
    };

    export const sendFriendGift = (
        playerState: IPlayerState,
        meta: {
            friendId: string;
            altGift?: boolean;
        }
    ): IPlayerState => {
        // Validate can send currency to friend.
        let friendData;
        ({ playerState, friendData } = getFriendData(playerState, meta.friendId));
        if ((meta.altGift && friendData.altGiftCooldown != null) || (!meta.altGift && friendData.giftCooldown != null))
            return playerState;
        playerState = PlayerStateHelper.updateTimers(playerState);
        playerState = InventoryStateHelper.giveReward(playerState, FRIEND_GIFT_REWARD, { queueTask: false, dontTrack: true});

        // Increment currency-share-count.
        playerState = ProgressStateHelper.setCurrencyShareCount(
            playerState,
            playerState.progress.currencyShareCount + 1
        );

        // Update friend data.
        return {
            ...playerState,
            social: {
                ...playerState.social,
                friendData: playerState.social.friendData.map(friend => {
                    if (friend.playerId !== meta.friendId) {
                        return friend;
                    }

                    if (meta.altGift) {
                        return {
                            ...friend,
                            altGiftCooldown: DateHelper.now.getTime(),
                        };
                    }

                    return {
                        ...friend,
                        giftCooldown: DateHelper.now.getTime(),
                    };
                }),
            },
        };
    };

    export const requestCurrency = (playerState: IPlayerState, friendId: string): IPlayerState => {
        // Validate can request currency from friend.
        let friendData;
        ({ playerState, friendData } = SocialStateHelper.getFriendData(playerState, friendId));
        if (friendData.currencyRequestTimer !== null) return playerState;

        if (playerState.timers.get(TimerType.GIFT) === null) {
            // Increment currency-share-count.
            playerState = ProgressStateHelper.setCurrencyShareCount(
                playerState,
                playerState.progress.currencyShareCount + 1
            );
        }

        playerState = PlayerStateHelper.updateTimers(playerState);

        // Update friend data.
        return {
            ...playerState,
            social: {
                ...playerState.social,
                friendData: playerState.social.friendData.map(friend => {
                    if (friend.playerId !== friendId) {
                        return friend;
                    }

                    return {
                        ...friend,
                        currencyRequestTimer: DateHelper.now.getTime(),
                    };
                }),
            },
        };
    };

    export const sendCurrency = (playerState: IPlayerState, friendId: string): IPlayerState => {
        // Validate can send currency to friend.
        let friendData;
        ({ playerState, friendData } = SocialStateHelper.getFriendData(playerState, friendId));
        if (friendData.currencySendTimer !== null) return playerState;

        if (playerState.timers.get(TimerType.GIFT) === null) {
            // Increment currency-share-count if the share prize timer is not on cooldown.
            playerState = ProgressStateHelper.setCurrencyShareCount(
                playerState,
                playerState.progress.currencyShareCount + 1
            );
        }

        playerState = PlayerStateHelper.updateTimers(playerState);
        // Update friend data.
        return {
            ...playerState,
            social: {
                ...playerState.social,
                friendData: playerState.social.friendData.map(friend => {
                    if (friend.playerId !== friendId) {
                        return friend;
                    }

                    return {
                        ...friend,
                        currencySendTimer: DateHelper.now.getTime(),
                    };
                }),
            },
        };
    };

    export const setFriendGiftTimer = (playerState: IPlayerState, friendId: string): IPlayerState => {
        // Create friend data if does not exist
        if (playerState.social.friendData.find(friend => friend.playerId === friendId) === undefined) {
            const newFriendData: IFriendData = {
                playerId: friendId,
                currencySendTimer: null,
                currencyRequestTimer: null,
                giftCooldown: DateHelper.now.getTime(),
            };
            playerState.social.friendData.push(newFriendData);
        }
        // Update friend data if already exists
        playerState = {
            ...playerState,
            social: {
                ...playerState.social,
                friendData: playerState.social.friendData.map(friend => {
                    if (friend.playerId !== friendId) {
                        return friend;
                    }

                    return {
                        ...friend,
                        giftCooldown: DateHelper.now.getTime(),
                    };
                }),
            },
        };
        return playerState;
    };

    export const updateFriendTimers = (playerState: IPlayerState): IPlayerState => {
        const friendData = playerState.social.friendData.map(friendData => {
            const currencySendTimer = restoreDate(friendData.currencySendTimer);
            if (!currencySendTimer || DateHelper.timeSince(currencySendTimer) > FRIEND_GIFT_TIMER_COOLDOWN) {
                friendData = {
                    ...friendData,
                    currencySendTimer: null,
                };
            }

            const currencyRequestTimer = restoreDate(friendData.currencyRequestTimer);
            if (!currencyRequestTimer || DateHelper.timeSince(currencyRequestTimer) > FRIEND_GIFT_TIMER_COOLDOWN) {
                friendData = {
                    ...friendData,
                    currencyRequestTimer: null,
                };
            }

            const giftCooldown = restoreDate(friendData.giftCooldown);
            if (!giftCooldown || DateHelper.timeSince(giftCooldown) > FRIEND_GIFT_TIMER_COOLDOWN) {
                friendData = {
                    ...friendData,
                    giftCooldown: null,
                };
            }

            const altGiftCooldown = restoreDate(friendData.altGiftCooldown);
            if (!altGiftCooldown || DateHelper.timeSince(altGiftCooldown) > FRIEND_ALT_GIFT_TIMER_COOLDOWN) {
                friendData = {
                    ...friendData,
                    altGiftCooldown: null,
                };
            }

            return friendData;
        });

        return {
            ...playerState,
            social: {
                ...playerState.social,
                friendData: friendData,
            },
        };
    };

    export const retireMessage = (playerState: IPlayerState, messageId: string): IPlayerState => {
        if (hasReceivedMessage(playerState, messageId)) {
            return playerState;
        } else {
            return {
                ...playerState,
                social: {
                    ...playerState.social,
                    receivedMessages: [...playerState.social.receivedMessages, messageId],
                },
            };
        }
    };

    export const hasReceivedMessage = (state: IPlayerState, messageId: string): boolean => {
        return state.social.receivedMessages.includes(messageId);
    };
}
